import { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  MapPin,
  Users,
  Car,
  LogOut,
  RefreshCw,
  LayoutDashboard,
  Menu,
  X,
  Phone,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  Wallet,
  ArrowDownLeft,
  TrendingUp,
  IndianRupee,
  Save,
  FileText,
  BarChart2,
  AlertTriangle,
  Download,
  Settings,
  MessageCircle,
  Flame,
  Bell,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CityAdminRidesManagement } from "./components/CityAdminRidesManagement";
import { CityAdminDriversManagement } from "./components/CityAdminDriversManagement";
import { CityAdminCommissionReport } from "./components/CityAdminCommissionReport";
import { CityAdminCancelledAnalytics } from "./components/CityAdminCancelledAnalytics";
import { CityAdminCustomersReport } from "./components/CityAdminCustomersReport";
import { CityAdminMyEarnings } from "./components/CityAdminMyEarnings";
import { CityAdminVehicleSettings } from "./components/CityAdminVehicleSettings";
import { DriversAffectedByCancellations } from "../admin/components/DriversAffectedByCancellations";
import { RideHeatMap } from "../admin/components/RideHeatMap";
import { NotificationsManagement } from "../admin/components/NotificationsManagement";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

const CityAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cityInfo, setCityInfo] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rides, setRides] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_drivers: 0,
    online_drivers: 0,
    total_customers: 0,
    total_rides: 0,
    pending_rides: 0,
    completed_rides: 0,
    total_earnings: 0,
  });
  
  // WhatsApp Support state
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [supportMessage, setSupportMessage] = useState("I need help");
  const [savingSupport, setSavingSupport] = useState(false);

  useEffect(() => {
    if (user?.role !== "city_admin") {
      navigate("/");
      return;
    }
    fetchData();
    fetchSupportSettings();
  }, [user]);
  
  const fetchSupportSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/support/${user.id}`);
      setWhatsappNumber(res.data.whatsapp_number || "");
      setSupportMessage(res.data.support_message || "I need help");
    } catch (err) {
      // No support settings found - using defaults
    }
  };

  const handleSaveSupport = async () => {
    setSavingSupport(true);
    try {
      await axios.put(`${API}/settings/support/${user.id}`, {
        whatsapp_number: whatsappNumber || null,
        support_message: supportMessage
      });
      toast.success("Support settings saved!");
    } catch (err) {
      toast.error("Failed to save support settings");
    } finally {
      setSavingSupport(false);
    }
  };

  const fetchData = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [cityRes, driversRes, ridesRes, earningsRes, customersRes] = await Promise.all([
        axios.get(`${API}/city-admin/${user.id}/my-city`),
        axios.get(`${API}/city-admin/${user.id}/drivers`),
        axios.get(`${API}/city-admin/${user.id}/rides`),
        axios.get(`${API}/city-admin/${user.id}/earnings`).catch(() => ({ data: null })),
        axios.get(`${API}/city-admin/${user.id}/customers`).catch(() => ({ data: [] })),
      ]);
      
      setCityInfo(cityRes.data);
      setDrivers(driversRes.data);
      setRides(ridesRes.data);
      setEarnings(earningsRes.data);
      setCustomers(customersRes.data || []);
      
      // Calculate stats
      const onlineDrivers = driversRes.data.filter(d => d.is_online).length;
      const pendingRides = ridesRes.data.filter(r => r.status === "pending").length;
      const completedRides = ridesRes.data.filter(r => r.status === "completed").length;
      const totalEarnings = ridesRes.data
        .filter(r => r.status === "completed")
        .reduce((sum, r) => sum + (r.fare || 0), 0);
      
      setStats({
        total_drivers: driversRes.data.length,
        online_drivers: onlineDrivers,
        total_customers: (customersRes.data || []).length,
        total_rides: ridesRes.data.length,
        pending_rides: pendingRides,
        completed_rides: completedRides,
        total_earnings: totalEarnings,
      });
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (!user || user.role !== "city_admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Access denied. City Admin only.</p>
      </div>
    );
  }

  const navItems = [
    { id: "overview", icon: LayoutDashboard, label: "Overview", path: "/city-admin" },
    { id: "heatmap", icon: Flame, label: "Heatmap", path: "/city-admin/heatmap" },
    { id: "notifications", icon: Bell, label: "Notifications", path: "/city-admin/notifications" },
    { id: "drivers", icon: Users, label: "Drivers", path: "/city-admin/drivers" },
    { id: "customers", icon: Users, label: "Customers", path: "/city-admin/customers" },
    { id: "rides", icon: Car, label: "Rides", path: "/city-admin/rides" },
    { id: "commission-report", icon: BarChart2, label: "Commission Report", path: "/city-admin/commission-report" },
    { id: "cancelled", icon: XCircle, label: "Cancelled Analytics", path: "/city-admin/cancelled" },
    { id: "affected-drivers", icon: AlertTriangle, label: "Affected Drivers", path: "/city-admin/affected-drivers" },
    { id: "pricing", icon: IndianRupee, label: "Pricing", path: "/city-admin/pricing" },
    { id: "earnings", icon: Wallet, label: "My Earnings", path: "/city-admin/earnings" },
    { id: "settings", icon: Settings, label: "Settings", path: "/city-admin/settings" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Impersonation Banner */}
      <ImpersonationBanner />
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b z-50 flex items-center px-4">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="ml-4">
          <h1 className="font-bold">City Admin</h1>
          <p className="text-xs text-muted-foreground">{cityInfo?.city?.name || "Loading..."}</p>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-card border-r z-40 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <MapPin className="w-6 h-6 text-primary" />
            <div>
              <h2 className="font-bold">City Admin</h2>
              <p className="text-xs text-muted-foreground">{cityInfo?.city?.name || "No city assigned"}</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === "/city-admin"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive ? "bg-primary text-white" : "hover:bg-muted"
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-bold">{user?.name?.[0] || "?"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">City Admin</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">
          <Routes>
            <Route index element={
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h1 className="text-2xl font-bold">Dashboard Overview</h1>
                    <p className="text-muted-foreground">
                      Managing {cityInfo?.city?.name || "your city"}
                    </p>
                  </div>
                  <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                  <div className="p-4 bg-card rounded-xl border">
                    <Users className="w-6 h-6 text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.total_drivers}</p>
                    <p className="text-xs text-muted-foreground">Drivers</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <div className="w-6 h-6 bg-green-500 rounded-full mb-2" />
                    <p className="text-2xl font-bold">{stats.online_drivers}</p>
                    <p className="text-xs text-muted-foreground">Online</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <Users className="w-6 h-6 text-indigo-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.total_customers}</p>
                    <p className="text-xs text-muted-foreground">Customers</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <Car className="w-6 h-6 text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.total_rides}</p>
                    <p className="text-xs text-muted-foreground">Rides</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <Clock className="w-6 h-6 text-yellow-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.pending_rides}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.completed_rides}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                  <div className="p-4 bg-card rounded-xl border">
                    <DollarSign className="w-6 h-6 text-primary mb-2" />
                    <p className="text-2xl font-bold">₹{stats.total_earnings}</p>
                    <p className="text-xs text-muted-foreground">Earnings</p>
                  </div>
                </div>

                {/* Recent Rides */}
                <div className="bg-card rounded-xl border p-4">
                  <h3 className="font-semibold mb-4">Recent Rides</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Customer</TableHead>
                          <TableHead>Driver</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Fare</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rides.slice(0, 10).map((ride) => (
                          <TableRow key={ride.id}>
                            <TableCell>{ride.customer_name}</TableCell>
                            <TableCell>{ride.driver_name || '-'}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                ride.status === 'completed' ? 'bg-green-100 text-green-700' :
                                ride.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                ride.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {ride.status}
                              </span>
                            </TableCell>
                            <TableCell>₹{ride.fare}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(ride.created_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            } />

            <Route path="drivers" element={
              <CityAdminDriversManagement cityName={cityInfo?.city?.name} />
            } />

            <Route path="customers" element={<CityAdminCustomersReport />} />

            <Route path="rides" element={
              <CityAdminRidesManagement 
                cityAdminId={user?.id} 
                cityName={cityInfo?.city?.name}
              />
            } />

            <Route path="pricing" element={
              <CityAdminVehicleSettings cityInfo={cityInfo} />
            } />

            <Route path="commission-report" element={<CityAdminCommissionReport />} />
            
            <Route path="cancelled" element={<CityAdminCancelledAnalytics />} />

            <Route path="affected-drivers" element={<DriversAffectedByCancellations isCityAdmin={true} />} />

            <Route path="earnings" element={<CityAdminMyEarnings />} />
            
            <Route path="heatmap" element={<RideHeatMap isCityAdmin={true} />} />
            
            <Route path="notifications" element={
              <NotificationsManagement 
                adminId={user?.id} 
                adminRole={user?.role}
                assignedCity={user?.assigned_city}
              />
            } />
            
            <Route path="settings" element={
              <div>
                <h1 className="text-2xl font-bold mb-6">Settings</h1>
                
                {/* WhatsApp Support Section */}
                <div className="bg-card rounded-xl border p-6 max-w-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">WhatsApp Support</h3>
                      <p className="text-sm text-muted-foreground">Allow drivers and customers to contact you via WhatsApp</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label>WhatsApp Number</Label>
                      <Input
                        type="tel"
                        value={whatsappNumber}
                        onChange={(e) => setWhatsappNumber(e.target.value)}
                        placeholder="91XXXXXXXXXX (with country code)"
                        className="mt-1"
                        data-testid="city-admin-whatsapp-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter your WhatsApp number with country code (e.g., 919876543210 for India)
                      </p>
                    </div>
                    <div>
                      <Label>Default Support Message</Label>
                      <Input
                        type="text"
                        value={supportMessage}
                        onChange={(e) => setSupportMessage(e.target.value)}
                        placeholder="I need help"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pre-filled message when someone contacts you
                      </p>
                    </div>
                    <Button 
                      onClick={handleSaveSupport} 
                      disabled={savingSupport}
                      className="mt-2"
                      data-testid="city-admin-save-support-btn"
                    >
                      {savingSupport ? "Saving..." : "Save Support Settings"}
                    </Button>
                  </div>
                </div>
              </div>
            } />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default CityAdminDashboard;
